/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../../../logger';
import { AdCommunicatorEvents } from '../../../messaging';
import type { Facts } from '../../../rules';
import { isGptAvailable } from '../../../util';
import { Page } from '../../page';
import { Placement } from '../../placement';
import { GptPlacement } from './placement';

import type { CampaignData } from '../../../types/';
import type { PrebidAdUnit } from '../../../types/prebid';
import type { SiteConfig } from '../../config';

const logger = Logger.getInstance();

class GptPage extends Page {
  private static gptInstance: GptPage;

  private constructor(siteConfig: SiteConfig) {
    super(siteConfig);
  }

  protected createPlacement(
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig,
  ): Placement {
    return new GptPlacement(adUnit, adUnitTransformer, campaignData, siteConfig);
  }

  /**
   * Extracts GPT page-level targeting via googletag.pubads().getTargetingKeys()
   * and googletag.pubads().getTargeting(key)
   * Returns normalized facts with 'page.kvs.' prefix
   */
  protected extractAdServerTargeting(): Facts {
    try {
      if (window.googletag && typeof window.googletag.pubads === 'function') {
        const pubads = window.googletag.pubads();
        if (
          typeof pubads.getTargetingKeys === 'function' &&
          typeof pubads.getTargeting === 'function'
        ) {
          const keys = pubads.getTargetingKeys();
          const targeting: Record<string, string[]> = {};
          for (const key of keys) {
            targeting[key] = pubads.getTargeting(key);
          }
          return this.normalizeAdServerTargeting(targeting);
        }
      }
    } catch (error) {
      logger.warn(`Error extracting GPT ad server targeting: ${error}`);
    }
    return {};
  }

  protected locateContainingPlacement(event: {
    data: AdCommunicatorEvents['TaggedAdServed'];
  }): Placement | null {
    let placement = super.locateContainingPlacement(event);
    if (placement) {
      return placement;
    }

    // First GPT fallback: converting adUnitPath to adUnitCode
    const adUnitCodeFromPath = event.data.adUnitPath?.split('/').pop()?.trim() ?? '';
    if (this.placementMap.has(adUnitCodeFromPath)) {
      const placement = this.placementMap.get(adUnitCodeFromPath) as Placement;
      logger.info(
        `TaggedAdServed event path-to-code mapping match found for adUnit: ${placement.adUnitCode}`,
      );
      return placement;
    }

    // Second fallback: GPT API Slot matching
    if (isGptAvailable()) {
      let matchingSlot: googletag.Slot | undefined = window.googletag
        .pubads()
        .getSlots()
        .find((slot: googletag.Slot) => {
          return slot.getAdUnitPath() === event.data.adUnitPath;
        });
      if (matchingSlot) {
        for (const placement of this.placementMap.values()) {
          if (placement.nativeSlotObject === matchingSlot) {
            logger.info(
              `TaggedAdServed event GPT slot ref match found for adUnit: ${placement.adUnitCode}`,
            );
            return placement;
          }
        }
      }
    }

    return null;
  }

  public static getInstance(siteConfig: SiteConfig): GptPage {
    if (!GptPage.gptInstance) {
      GptPage.gptInstance = new GptPage(siteConfig);
    }
    return GptPage.gptInstance;
  }
}

export { GptPage };
